import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/api/client";
import { toast } from "sonner";
import { Target, Plus, Trash2, ArrowLeft, CheckCircle2, AlertTriangle, TrendingDown } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const TARGET_TYPES = [
  { value: "net_zero",   label: "Net Zero" },
  { value: "sci_based",  label: "Science-Based Target (SBTi)" },
  { value: "interim",    label: "Interim Reduction Target" },
  { value: "custom",     label: "Custom Target" },
];

export default function CorporateTargets() {
  const navigate = useNavigate();
  const [gapData, setGapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", target_type: "interim", base_year: 2023,
    base_year_tco2e: "", target_year: 2030, target_reduction_pct: 50, notes: "" });
  const [saving, setSaving] = useState(false);
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id || 1;

  const load = async () => {
    setLoading(true);
    try {
      const res = await client.get(`/corporate/orgs/${orgId}/gap-analysis`);
      setGapData(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post(`/corporate/orgs/${orgId}/targets`, {
        ...form,
        base_year: parseInt(form.base_year),
        base_year_tco2e: parseFloat(form.base_year_tco2e),
        target_year: parseInt(form.target_year),
        target_reduction_pct: parseFloat(form.target_reduction_pct),
      });
      toast.success("Target created");
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this target?")) return;
    try { await client.delete(`/corporate/targets/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <div className="p-8"><div className="h-64 bg-white border border-slate-200 rounded-xl animate-pulse" /></div>;

  const targets = gapData?.targets || [];
  const currentTco2e = gapData?.current_tco2e;

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in">
      <div className="flex items-start justify-between">
          
      <PageHeader
        title="Corporate Targets"
        subtitle="Set and track corporate climate targets — Net Zero, SBTi, interim milestones."
        backUrl="/corporate"
        backLabel="Corporate Dashboard"
      />
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B]"
          data-testid="create-target-btn">
          <Plus size={14} /> Set Target
        </button>
      </div>

      {currentTco2e !== undefined && (
        <MetricCard
          label="Current Total Emissions"
          value={currentTco2e?.toFixed(2)}
          unit="tCO₂e"
          sub="Current reporting year"
          color="text-slate-900"
        />
      )}

      <div className="space-y-6" data-testid="targets-list">
        {targets.length === 0 ? (
          <SectionCard>
            <div className="py-10 text-center">
              <Target size={32} className="mx-auto mb-3 text-slate-200" />
              <p className="text-sm text-slate-500">No targets set</p>
              <CTAButton variant="ghost" onClick={() => setShowCreate(true)} className="mt-3 text-xs text-[#059669]">Set first target</CTAButton>
            </div>
          </SectionCard>
        ) : targets.map(t => {
          const onTrack = t.on_track;
          const glidePath = t.glide_path || [];
          const chartData = glidePath.map(g => ({
            year: String(g.year), target: g.target_tco2e,
            ...(g.year === gapData.reporting_year ? { actual: currentTco2e } : {})
          }));

          return (
            <SectionCard key={t.id} noPadding>
              <div className="px-6 py-5 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-base font-semibold text-slate-900">{t.name}</h3>
                      <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full capitalize">
                        {t.target_type?.replace("_", " ")}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Base: {t.base_year} ({t.base_year_tco2e?.toFixed(1)} tCO₂e) → Target: {t.target_year} ({t.target_tco2e?.toFixed(1)} tCO₂e, {t.target_reduction_pct}% reduction)
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${onTrack ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {onTrack ? <><CheckCircle2 size={11} /> On Track</> : <><AlertTriangle size={11} /> Off Track</>}
                    </span>
                    <button onClick={() => del(t.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-6">
                {/* Gap metrics */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Gap Analysis — {gapData.reporting_year}</p>
                  {[
                    { label: "Current Emissions",  value: `${currentTco2e?.toFixed(2)} tCO₂e`, color: "text-slate-900" },
                    { label: "Expected on Glide Path", value: t.expected_tco2e ? `${t.expected_tco2e?.toFixed(2)} tCO₂e` : "—", color: "text-slate-600" },
                    { label: "Gap to Target Path",    value: t.gap_tco2e != null ? `${t.gap_tco2e > 0 ? "+" : ""}${t.gap_tco2e?.toFixed(2)} tCO₂e` : "—",
                      color: t.gap_tco2e > 0 ? "text-amber-600" : "text-emerald-600" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="flex justify-between items-center py-2 border-b border-slate-50 last:border-0">
                      <span className="text-xs text-slate-500">{label}</span>
                      <span className={`text-sm font-semibold tabular-nums ${color}`}>{value}</span>
                    </div>
                  ))}
                  {t.gap_tco2e > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                      <strong>{t.gap_tco2e.toFixed(2)} tCO₂e above glide path.</strong> Planned initiatives could close {Math.min(100, ((53.75 / t.gap_tco2e) * 100)).toFixed(0)}% of the gap if implemented.
                    </div>
                  )}
                </div>
                {/* Glide path chart */}
                {chartData.length >= 2 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Glide Path</p>
                    <ResponsiveContainer width="100%" height={140}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="year" tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={36} />
                        <Tooltip contentStyle={{ background: "white", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: 11 }}
                          formatter={(v, name) => [`${v?.toFixed(1)} tCO₂e`, name === "target" ? "Target" : "Actual"]} />
                        <Line type="monotone" dataKey="target" stroke="#059669" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Target" />
                        <Line type="monotone" dataKey="actual" stroke="#0F172A" strokeWidth={2} dot={{ r: 5, fill: "#0F172A" }} name="Actual" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </SectionCard>
          );
        })}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Set Corporate Target</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Target Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required
                className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]"
                placeholder="e.g. Net Zero 2050" data-testid="target-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Target Type</label>
              <select value={form.target_type} onChange={e => setForm(f => ({...f, target_type: e.target.value}))}
                className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#059669]">
                {TARGET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Base Year</label>
                <input type="number" value={form.base_year} onChange={e => setForm(f => ({...f, base_year: e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Base Year Emissions (tCO₂e)</label>
                <input type="number" step="0.01" value={form.base_year_tco2e} onChange={e => setForm(f => ({...f, base_year_tco2e: e.target.value}))} required
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" placeholder="e.g. 97.59" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Target Year</label>
                <input type="number" value={form.target_year} onChange={e => setForm(f => ({...f, target_year: e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-slate-500 mb-1.5">Reduction %</label>
                <input type="number" step="1" min="1" max="100" value={form.target_reduction_pct} onChange={e => setForm(f => ({...f, target_reduction_pct: e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]" placeholder="50" />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowCreate(false)} className="flex-1 h-10 border border-slate-300 text-sm rounded-lg hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 h-10 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-[#1E293B] disabled:opacity-60" data-testid="target-submit-btn">
                {saving ? "Setting..." : "Set Target"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
